<template>
  <Teleport to="body">
    <Transition name="onboarding-fade">
      <div v-if="visible" class="onboarding-overlay" @click.self="handleBackdropClick">
        <div class="onboarding-modal" :class="{ 'is-mobile': isMobileDevice }">
          <div class="onboarding-bg">
            <img :src="bgImage" alt="" />
          </div>
          <div class="onboarding-bg-overlay" />

          <button class="onboarding-skip" @click="skip">
            {{ localize('onboarding.skip') }}
          </button>

          <div class="onboarding-content">
            <div class="onboarding-top">
              <Transition name="cta-fade">
                <div v-if="step === 'leave_home'" class="onboarding-intro-icon" @click="advance">
                  <div class="walk-target">
                    <GamIcon :name="GamIconName.GAMBIT" size="28px" />
                  </div>
                </div>
              </Transition>

              <Transition name="board-enter">
                <div v-if="showBoard" class="onboarding-board-group">
                  <div class="onboarding-board">
                    <GamChessboard
                      ref="boardRef"
                      :fen="STARTING_FEN"
                      :orientation="GamChessboardOrientation.WHITE"
                      :is-interactive="boardInteractive"
                      :is-animated="true"
                      @move="onUserMove"
                    />
                    <Transition name="turn-fade">
                      <div v-if="showTurnIndicator" class="onboarding-turn">
                        <div class="turn-dot white" />
                        <span class="turn-label">{{ localize('onboarding.turn.white') }}</span>
                      </div>
                    </Transition>
                  </div>
                </div>
              </Transition>
            </div>

            <div class="onboarding-bottom">
              <Transition name="text-fade" mode="out-in">
                <div :key="step" class="onboarding-text" @click="advance">
                  <h2 class="onboarding-headline">{{ headline }}</h2>
                  <p v-if="subtitle" class="onboarding-subtitle">{{ subtitle }}</p>
                </div>
              </Transition>
              <Transition name="cta-appear">
                <button v-if="showFinalCtas" class="onboarding-cta" @click="complete">
                  {{ localize('onboarding.cta') }}
                </button>
              </Transition>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import GamChessboard from '@/views/components/gam-chessboard/GamChessboard.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamChessboardOrientation } from '@/views/composables/constants/components/gamChessboard.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import type { Square } from 'chess.js';
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';

const bgImage = new URL('../../../ui/images/auth/onboarding-bg.png', import.meta.url).href;

const STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

type OnboardingStep = 'leave_home' | 'meet_board' | 'conversation';

const AUTO_RESPONSE_MOVES: Array<{ from: Square; to: Square }> = [
  { from: 'e7', to: 'e5' },
  { from: 'd7', to: 'd5' },
  { from: 'b8', to: 'c6' },
  { from: 'g8', to: 'f6' },
];

withDefaults(
  defineProps<{
    visible?: boolean;
  }>(),
  { visible: true },
);

const emit = defineEmits<{
  complete: [];
  skip: [];
}>();

const step = ref<OnboardingStep>('leave_home');
const boardRef = ref<InstanceType<typeof GamChessboard> | null>(null);
const userMoveCount = ref(0);
const autoPlayTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const autoAdvanceTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const isMobileDevice = isMobile;

autoAdvanceTimer.value = setTimeout(() => {
  if (step.value === 'leave_home') {
    advance();
  }
}, 10000);

const HEADLINES: Record<OnboardingStep, string> = {
  leave_home: 'onboarding.step1.title',
  meet_board: 'onboarding.step2.title',
  conversation: 'onboarding.step3.title',
};

const SUBTITLES: Record<OnboardingStep, string> = {
  leave_home: 'onboarding.step1.subtitle',
  meet_board: 'onboarding.step2.subtitle',
  conversation: 'onboarding.step3.subtitle',
};

const headline = computed(() => localize(HEADLINES[step.value]));

const subtitle = computed(() => {
  const key = SUBTITLES[step.value];
  return key ? localize(key) : '';
});

const showBoard = computed(() => step.value === 'meet_board' || step.value === 'conversation');

const showFinalCtas = ref(false);

const showTurnIndicator = computed(() => step.value === 'meet_board' && userMoveCount.value === 0);

const boardInteractive = computed(() => {
  if (step.value === 'meet_board') return true;
  return false;
});

function advance() {
  if (step.value === 'leave_home') {
    if (autoAdvanceTimer.value) {
      clearTimeout(autoAdvanceTimer.value);
      autoAdvanceTimer.value = null;
    }
    step.value = 'meet_board';
  }
}

async function onUserMove() {
  userMoveCount.value++;

  if (userMoveCount.value === 1 && step.value === 'meet_board') {
    await nextTick();
    autoPlayTimer.value = setTimeout(() => {
      step.value = 'conversation';
      autoPlayTimer.value = setTimeout(() => {
        playAutoResponse(0);
        autoPlayTimer.value = setTimeout(() => {
          showFinalCtas.value = true;
        }, 1000);
      }, 600);
    }, 400);
  }
}

function playAutoResponse(index: number) {
  const board = boardRef.value;
  if (!board || index >= AUTO_RESPONSE_MOVES.length) return;

  const move = AUTO_RESPONSE_MOVES[index];
  const piece = getPieceAt(move.from);
  if (!piece) return;

  board.lastMoveFrom = move.from;
  board.lastMoveTo = move.to;
  board.animateMove(move.from, move.to, piece, 350).then(() => {
    board.engine.makeMove(move.from, move.to);
  });
}

function getPieceAt(square: Square): string | null {
  const board = boardRef.value;
  if (!board) return null;

  const boardData = board.engine.board.value;
  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
  const ranks = ['8', '7', '6', '5', '4', '3', '2', '1'];
  const col = files.indexOf(square[0]);
  const row = ranks.indexOf(square[1]);
  if (col < 0 || row < 0) return null;

  const cell = boardData[row][col];
  if (!cell) return null;
  return cell.color === 'w' ? cell.type.toUpperCase() : cell.type.toLowerCase();
}

function complete() {
  emit('complete');
}

function skip() {
  emit('skip');
}

function handleBackdropClick() {
  // Intentionally empty -- don't close on backdrop click during onboarding
}

onBeforeUnmount(() => {
  if (autoPlayTimer.value) {
    clearTimeout(autoPlayTimer.value);
  }
  if (autoAdvanceTimer.value) {
    clearTimeout(autoAdvanceTimer.value);
  }
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.onboarding-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-linear-gradient-vertical) var(--color-dark-700);
  background-attachment: fixed;
}

.onboarding-modal {
  position: relative;
  width: 400px;
  max-width: calc(100vw - 2rem);
  height: min(720px, calc(100vh - 3rem));
  border-radius: 1.5rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &.is-mobile {
    width: 100%;
    max-width: 100%;
    height: 100%;
    border-radius: 0;
  }
}

.onboarding-bg {
  position: absolute;
  inset: 0;
  z-index: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 65%;
  }
}

.onboarding-bg-overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(0, 0, 0, 0.1) 30%,
    rgba(0, 0, 0, 0.15) 60%,
    rgba(0, 0, 0, 0.5) 100%
  );
}

.onboarding-skip {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  transition: color 200ms ease;

  @include hover {
    color: rgba(255, 255, 255, 0.8);
  }
}

.onboarding-content {
  position: relative;
  z-index: 5;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 0;
}

.onboarding-top {
  position: relative;
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 3.5rem 1.5rem 0;
  width: 100%;
}

.onboarding-bottom {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  padding: 1.5rem 1.5rem 2rem;
  width: 100%;
  height: 130px;
  flex-shrink: 0;
}

.onboarding-text {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  cursor: default;
}

.onboarding-headline {
  font-size: 26px;
  font-weight: 700;
  color: #fff;
  line-height: 1.15;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
  margin: 0;
}

.onboarding-subtitle {
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.4;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
  margin: 0;
}

.onboarding-intro-icon {
  position: absolute;
  top: 70%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.onboarding-board-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.onboarding-board {
  position: relative;
  width: 100%;
  max-width: 280px;
}

.onboarding-turn {
  position: absolute;
  bottom: -1.5rem;
  left: 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;

  .turn-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    transition: background 0.2s ease;

    &.white {
      background: #fff;
      box-shadow: 0 0 6px rgba(255, 255, 255, 0.5);
    }
  }

  .turn-label {
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.05em;
    opacity: 0.7;
  }
}

.onboarding-cta {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 2rem;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  padding: 0.75rem 2rem;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: all 200ms ease;
  letter-spacing: 0.02em;

  @include hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
  }

  @include press;
}

.walk-target {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: var(--color-linear-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  animation: board-float 2.5s ease-in-out infinite;
  box-shadow:
    0 2px 8px 0 var(--color-pink-30),
    0 0 16px 2px var(--color-pink-20);

  :deep(svg) {
    fill: white;
  }
}

@keyframes board-float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-4px);
  }
}

// Transitions

.onboarding-fade-enter-active,
.onboarding-fade-leave-active {
  transition: opacity 400ms ease;
}

.onboarding-fade-enter-from,
.onboarding-fade-leave-to {
  opacity: 0;
}

.text-fade-enter-active,
.text-fade-leave-active {
  transition: all 500ms ease;
}

.text-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.text-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.board-enter-enter-active {
  transition: all 600ms ease;
}

.board-enter-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.turn-fade-leave-active {
  transition: opacity 400ms ease;
}

.turn-fade-leave-to {
  opacity: 0;
}

.cta-appear-enter-active {
  transition: opacity 400ms ease;
}

.cta-appear-enter-from {
  opacity: 0;
}

.cta-fade-enter-active {
  transition: all 600ms ease;
}

.cta-fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
</style>
